import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Typography } from '@material-ui/core';
import PropTypes from 'prop-types';

import { TableListView, Chip } from 'components';
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';
import { SSO_SERVICES } from 'Services';
import Utils from 'Shared/Utils';

import { IDENTIFIER_TYPE, SSO_PROVIDER, capitalizeProvider } from './constants';
import AuthMethodsSection from './AuthMethodsSection';
import SSOConfigModal from './SSOConfigModal';
import SSOGroupMappingSection from './SSOGroupMappingSection';

const SSOSettings = ({ level, levelId, partnerId, groupId }) => {
  const { dispatch, state: { user } } = useContext(Context);

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editConfig, setEditConfig] = useState(null);
  const [mappingConfig, setMappingConfig] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [toggleLoadingId, setToggleLoadingId] = useState(null);

  useEffect(() => {
    fetchSSOConfigs();
  }, [level, levelId]);

  const fetchSSOConfigs = async () => {
    try {
      const response = await SSO_SERVICES.getSSOConfigs(level, levelId);

      if (Utils.checkIfSuccess(response)) {
        setConfigs(response?.data?.configs || []);
      } else {
        dispatch({
          type: ADD_NOTIFICATION,
          payload: {
            notification: {
              severity: 'error',
              message: response?.message || 'Failed to load SSO configuration',
            },
          },
        });
      }
    } catch (error) {
      console.error('Failed to load SSO configuration:', error);
      dispatch({
        type: ADD_NOTIFICATION,
        payload: {
          notification: {
            severity: 'error',
            message: 'Failed to load SSO configuration',
          },
        },
      });
    }
    setLoading(false);
  };

  const handleConfigUpdate = () => {
    setEditConfig(null);
    setIsCreateOpen(false);
    setLoading(true);
    fetchSSOConfigs();
  };

  const handleToggleActive = async (config) => {
    if (toggleLoadingId) return;
    setToggleLoadingId(config.id);
    const newStatus = !config.is_active;
    try {
      const response = await SSO_SERVICES.activateSSOConfig(config.id, newStatus);

      if (Utils.checkIfSuccess(response)) {
        dispatch({
          type: ADD_NOTIFICATION,
          payload: {
            notification: {
              severity: 'success',
              message: `SSO configuration ${newStatus ? 'activated' : 'deactivated'} successfully`,
            },
          },
        });
        fetchSSOConfigs();
      } else {
        dispatch({
          type: ADD_NOTIFICATION,
          payload: {
            notification: {
              severity: 'error',
              message: response?.data?.message || response?.message || 'Failed to update SSO status',
            },
          },
        });
      }
    } catch (error) {
      console.error('Failed to toggle SSO config:', error);
      dispatch({
        type: ADD_NOTIFICATION,
        payload: {
          notification: {
            severity: 'error',
            message: 'Failed to update SSO status',
          },
        },
      });
    }
    setToggleLoadingId(null);
  };

  const tableColumns = [
    {
      name: 'provider',
      label: 'Provider',
      options: {
        customBodyRender: (config) => {
          const label = capitalizeProvider(config.sso_provider);
          const chipStatus = config.is_active ? 'Active' : 'Inactive';
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {label}
              <Chip status={chipStatus} label={chipStatus} size="small" />
            </span>
          );
        },
      },
    },
    { name: 'credentials', label: 'Credentials' },
    { name: 'auto_create', label: 'Auto-Create Users' },
    { name: 'sync_roles', label: 'Sync on Login' },
    { name: 'auto_detect_mapping', label: 'Auto-Detect Groups' },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        sort: false,
        customBodyRender: (config) => (
          <>
            <Typography
              component="span"
              className="theme_color_orange cursor_pointer"
              onClick={(e) => {
                e.stopPropagation();
                setEditConfig(config);
              }}
              data-testid={`edit-sso-config-${config.id}`}
            >
              Edit
            </Typography>
            <Typography component="span" className="separator">
              |
            </Typography>
            <Typography
              component="span"
              className="theme_color_orange cursor_pointer"
              onClick={(e) => {
                e.stopPropagation();
                setMappingConfig(config);
              }}
              data-testid={`view-group-mapping-${config.id}`}
            >
              View Mappings
            </Typography>
            <Typography component="span" className="separator">
              |
            </Typography>
            <Typography
              component="span"
              className={`theme_color_orange ${toggleLoadingId === config.id ? '' : 'cursor_pointer'}`}
              style={toggleLoadingId === config.id ? { opacity: 0.5, pointerEvents: 'none' } : {}}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleActive(config);
              }}
              data-testid={`toggle-sso-config-${config.id}`}
            >
              {toggleLoadingId === config.id && (config.is_active ? 'Disabling...' : 'Enabling...')}
              {toggleLoadingId !== config.id && (config.is_active ? 'Disable' : 'Enable')}
            </Typography>
          </>
        ),
      },
    },
  ];

  const getCredentialsStatus = (config) => {
    if (!config.has_credentials) return 'Not Configured';
    // Google reads groups from the Admin SDK, so a JIT-enabled config
    // without a service account cannot be activated.
    if (
      config.sso_provider === SSO_PROVIDER.GOOGLE &&
      config.jit_enabled &&
      !config.has_service_account_key
    ) {
      return 'Configured (no directory access)';
    }
    return 'Configured';
  };

  const tableData = useMemo(
    () =>
      configs.map((config) => ({
        provider: config,
        credentials: getCredentialsStatus(config),
        auto_create: config.jit_enabled ? 'Enabled' : 'Disabled',
        sync_roles: config.jit_update_on_login ? 'Enabled' : 'Disabled',
        auto_detect_mapping: config.auto_detect_mapping ? 'Enabled' : 'Disabled',
        actions: config,
      })),
    [configs]
  );

  const tableOptions = {
    onRowClick: (_rowData, rowMeta) => {
      const config = configs[rowMeta.dataIndex];
      if (config) setEditConfig(config);
    },
  };

  // When a config is selected for mapping, show the mapping section instead
  if (mappingConfig) {
    return (
      <SSOGroupMappingSection
        level={level}
        levelId={levelId}
        partnerId={partnerId}
        groupId={groupId}
        ssoProvider={mappingConfig.sso_provider}
        identifierType={mappingConfig.idp_group_identifier_type || IDENTIFIER_TYPE.ID}
        onBack={() => setMappingConfig(null)}
      />
    );
  }

  return (
    <>
      {Utils.checkIfInternalUser(user) && (
        <AuthMethodsSection level={level} levelId={levelId} />
      )}

      <TableListView
        tableData={tableData}
        tableColumns={tableColumns}
        tableOptions={tableOptions}
        pagination={tableData.length > 10}
        headerSection={true}
        headerTitle="SSO Configuration"
        headerButton="+ Add Configuration"
        handleHeaderButtonClick={() => setIsCreateOpen(true)}
        emptyPlaceHolderContent="No SSO configurations found. Click '+ Add Configuration' to get started."
        isLoading={loading}
      />

      {(editConfig || isCreateOpen) && (
        <SSOConfigModal
          level={level}
          levelId={levelId}
          configData={editConfig || null}
          onClose={() => {
            setEditConfig(null);
            setIsCreateOpen(false);
          }}
          onUpdate={handleConfigUpdate}
        />
      )}
    </>
  );
};

SSOSettings.propTypes = {
  level: PropTypes.string.isRequired,
  levelId: PropTypes.string.isRequired,
  partnerId: PropTypes.string,
  groupId: PropTypes.string,
};

export default SSOSettings;
